import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { BLOG_POSTS, getPostMeta } from "@/lib/blog";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import CallStrip from "@/components/CallStrip";

const SITE_URL =
  (typeof window !== "undefined" && window.location.origin) ||
  "https://flowgridplumbing.com.au";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function BlogPage() {
  const posts = [...BLOG_POSTS].sort((a, b) => b.dateISO.localeCompare(a.dateISO));

  return (
    <>
      <SEO
        title="Plumbing Blog — Guides, Tips & Local Advice for Sydney"
        description="Honest plumbing guides from licensed Sydney plumbers — blocked drains, hot water, gas safety, slab leaks and emergency plumbing across Western Sydney and the Inner West."
        canonicalPath="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Flowgrid Plumbing Blog",
          url: `${SITE_URL}/blog`,
          blogPost: posts.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `${SITE_URL}/blog/${p.slug}`,
            datePublished: p.dateISO,
            description: p.excerpt,
          })),
        }}
      />

      <PageHeader
        eyebrow="Blog"
        title="Plumbing guides for real Sydney homes."
        subtitle="Honest, useful advice written by the licensed plumbers who actually do the work — no fluff, no SEO spam, no upsells."
      />

      <section className="bg-white py-20 md:py-24" data-testid="blog-list-section">
        <div className="fg-container">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => {
              const { service, suburb } = getPostMeta(p);
              const cover = p.coverImage || service?.image;
              return (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="fg-card-hover group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white"
                >
                  {cover && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img
                        src={cover}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700">
                      {suburb && <span>{suburb.name}</span>}
                      {service && suburb && <span className="text-slate-300">·</span>}
                      {service && <span className="text-slate-500">{service.title}</span>}
                    </div>
                    <h2 className="mt-3 font-display text-xl font-bold leading-snug tracking-tight text-slate-900 group-hover:text-blue-700">
                      {p.title}
                    </h2>
                    <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-600">
                      {p.excerpt}
                    </p>
                    <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5" /> {formatDate(p.dateISO)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" /> {p.readingMins} min
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-1 font-bold uppercase tracking-wider text-blue-700">
                        Read <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CallStrip />
    </>
  );
}
